import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Zap, ZapOff, Camera, RotateCcw, Check } from 'lucide-react';

interface ReceiptScannerProps {
  onCapture: (file: File) => void;
  onClose: () => void;
}

type ScannerPhase = 'scanning' | 'preview';

// ---- Image processing helpers -----------------------------------------------

/** Bilineares Skalieren auf maxBreite px (Seitenverhaeltnis beibehalten) */
function scaleCanvas(src: HTMLCanvasElement, maxWidth: number): HTMLCanvasElement {
  if (src.width <= maxWidth) return src;
  const ratio = maxWidth / src.width;
  const dst = document.createElement('canvas');
  dst.width = maxWidth;
  dst.height = Math.round(src.height * ratio);
  const ctx = dst.getContext('2d')!;
  ctx.drawImage(src, 0, 0, dst.width, dst.height);
  return dst;
}

/** Kontrast per ImageData anpassen (factor > 1 = mehr Kontrast) */
function applyContrast(canvas: HTMLCanvasElement, factor: number): void {
  const ctx = canvas.getContext('2d')!;
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const intercept = 128 * (1 - factor);
  for (let i = 0; i < data.length; i += 4) {
    data[i]     = Math.min(255, Math.max(0, data[i]     * factor + intercept));
    data[i + 1] = Math.min(255, Math.max(0, data[i + 1] * factor + intercept));
    data[i + 2] = Math.min(255, Math.max(0, data[i + 2] * factor + intercept));
  }
  ctx.putImageData(imageData, 0, 0);
}

/** Einfacher Schaerfe-Kernel (Unsharp Mask 3x3) */
function applySharpen(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext('2d')!;
  const { width, height } = canvas;
  const src = ctx.getImageData(0, 0, width, height);
  const dst = ctx.createImageData(width, height);
  const s = src.data;
  const d = dst.data;

  // Unsharp-Mask-Kernel: center=5, edges=-1, corners=0
  const kernel = [
     0, -1,  0,
    -1,  5, -1,
     0, -1,  0,
  ];

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = (y * width + x) * 4;
      for (let c = 0; c < 3; c++) {
        let val = 0;
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const kIdx = ((y + ky) * width + (x + kx)) * 4 + c;
            val += s[kIdx] * kernel[(ky + 1) * 3 + (kx + 1)];
          }
        }
        d[idx + c] = Math.min(255, Math.max(0, val));
      }
      d[idx + 3] = 255; // Alpha
    }
  }

  // Randpixel unveraendert uebernehmen
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (y === 0 || y === height - 1 || x === 0 || x === width - 1) {
        const idx = (y * width + x) * 4;
        d[idx]     = s[idx];
        d[idx + 1] = s[idx + 1];
        d[idx + 2] = s[idx + 2];
        d[idx + 3] = 255;
      }
    }
  }

  ctx.putImageData(dst, 0, 0);
}

/** Vollstaendige Client-seitige Vorverarbeitung — gibt Data-URL zurueck */
function processImage(videoEl: HTMLVideoElement): Promise<{ dataUrl: string; canvas: HTMLCanvasElement }> {
  return new Promise((resolve) => {
    // Rohbild in temporaeres Canvas uebertragen
    let canvas = document.createElement('canvas');
    canvas.width  = videoEl.videoWidth;
    canvas.height = videoEl.videoHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(videoEl, 0, 0);

    // 1. Skalieren auf max 2000 px
    canvas = scaleCanvas(canvas, 2000);

    // 2. Kontrast +30 % (factor = 1.3)
    applyContrast(canvas, 1.3);

    // 3. Schaerfen
    applySharpen(canvas);

    // 4. Als JPEG 85 % exportieren
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    resolve({ dataUrl, canvas });
  });
}

/** Data-URL in File-Objekt konvertieren */
function dataUrlToFile(dataUrl: string, filename: string): File {
  const [header, base64] = dataUrl.split(',');
  const mimeMatch = header.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], filename, { type: mime });
}

// ---- Komponente -------------------------------------------------------------

export const ReceiptScanner: React.FC<ReceiptScannerProps> = ({ onCapture, onClose }) => {
  const videoRef  = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [phase, setPhase]       = useState<ScannerPhase>('scanning');
  const [torchOn, setTorchOn]   = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [capturedFile, setCapturedFile] = useState<File | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Kamera starten
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width:  { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      // Taschenlampe verfuegbar?
      const track = stream.getVideoTracks()[0];
      const capabilities = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };
      setTorchAvailable(!!capabilities.torch);
    } catch {
      setCameraError('Kamera konnte nicht geöffnet werden. Bitte Berechtigungen prüfen.');
    }
  }, []);

  // Kamera stoppen
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Taschenlampe umschalten
  const toggleTorch = useCallback(async () => {
    if (!streamRef.current || !torchAvailable) return;
    const track = streamRef.current.getVideoTracks()[0];
    const next = !torchOn;
    try {
      await track.applyConstraints({
        advanced: [{ torch: next } as MediaTrackConstraintSet],
      });
      setTorchOn(next);
    } catch {
      // Torch evtl. doch nicht unterstuetzt — ignorieren
    }
  }, [torchOn, torchAvailable]);

  // Foto aufnehmen & verarbeiten
  const takePhoto = useCallback(async () => {
    if (!videoRef.current || isProcessing) return;
    setIsProcessing(true);

    try {
      const { dataUrl } = await processImage(videoRef.current);
      const timestamp   = new Date().toISOString().replace(/[:.]/g, '-');
      const file        = dataUrlToFile(dataUrl, `kassenzettel-${timestamp}.jpg`);

      // Vorschau
      setPreviewUrl(dataUrl);
      setCapturedFile(file);
      setPhase('preview');

      // Kamera kann jetzt stoppen (spart Akku)
      stopCamera();
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, stopCamera]);

  // "Nochmal" — Kamera neu starten
  const retake = useCallback(() => {
    if (previewUrl) {
      // DataURL aus dem Blob-Speicher des Browsers freigeben
      // (DataURLs belegen keinen separaten URL-Speicher, aber State bereinigen)
      setPreviewUrl(null);
    }
    setCapturedFile(null);
    setPhase('scanning');
    startCamera();
  }, [previewUrl, startCamera]);

  // "Verwenden"
  const handleUse = useCallback(() => {
    if (!capturedFile) return;
    stopCamera();
    onCapture(capturedFile);
  }, [capturedFile, onCapture, stopCamera]);

  // Schliessen
  const handleClose = useCallback(() => {
    stopCamera();
    onClose();
  }, [stopCamera, onClose]);

  // Cleanup bei Unmount
  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, [startCamera, stopCamera]);

  // Prevent body scroll while overlay offen
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 bg-black flex flex-col"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
      role="dialog"
      aria-modal="true"
      aria-label="Kassenzettel scannen"
    >
      {/* Verstecktes Canvas fuer Bildverarbeitung */}
      <canvas ref={canvasRef} className="hidden" aria-hidden="true" />

      {/* ------------------------------------------------------------------ */}
      {/* SCANNING PHASE                                                       */}
      {/* ------------------------------------------------------------------ */}
      {phase === 'scanning' && (
        <>
          {/* Kamera-Feed */}
          {!cameraError ? (
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-cover"
              playsInline
              muted
              autoPlay
              aria-hidden="true"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-neutral-900 px-8">
              <div className="text-center space-y-3">
                <Camera className="w-12 h-12 text-neutral-500 mx-auto" />
                <p className="text-white font-medium">{cameraError}</p>
              </div>
            </div>
          )}

          {/* Dunkles Overlay mit Ausschnitt (SVG-Clip) */}
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <ScanOverlay />
          </div>

          {/* Animierte Scan-Linie */}
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center" aria-hidden="true">
            <div className="relative" style={{ width: '72%', height: '55%' }}>
              <div className="scan-line absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary-400 to-transparent opacity-80" />
            </div>
          </div>

          {/* Top-Leiste */}
          <div className="relative z-10 flex items-center justify-between px-5 pt-4 pb-3">
            <button
              onClick={handleClose}
              className="w-11 h-11 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white
                         hover:bg-black/70 active:scale-95 transition-all duration-150"
              aria-label="Scanner schliessen"
            >
              <X className="w-5 h-5" />
            </button>

            <p className="text-white text-sm font-medium drop-shadow-lg select-none">
              Kassenzettel scannen
            </p>

            {torchAvailable ? (
              <button
                onClick={toggleTorch}
                className="w-11 h-11 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white
                           hover:bg-black/70 active:scale-95 transition-all duration-150"
                aria-label={torchOn ? 'Taschenlampe ausschalten' : 'Taschenlampe einschalten'}
                aria-pressed={torchOn}
              >
                {torchOn ? (
                  <Zap className="w-5 h-5 text-accent-400" />
                ) : (
                  <ZapOff className="w-5 h-5" />
                )}
              </button>
            ) : (
              // Platzhalter damit Top-Leiste zentriert bleibt
              <div className="w-11 h-11" aria-hidden="true" />
            )}
          </div>

          {/* Hinweis-Text */}
          <div className="relative z-10 flex-1 flex flex-col items-center justify-end pb-6 pointer-events-none">
            <p className="text-white/70 text-xs text-center px-8 mb-8 select-none drop-shadow">
              Halte die Kamera ruhig und positioniere den Bon im Rahmen
            </p>

            {/* Foto-Button */}
            <button
              onClick={takePhoto}
              disabled={isProcessing || !!cameraError}
              className="pointer-events-auto w-20 h-20 rounded-full
                         bg-white flex items-center justify-center
                         shadow-2xl hover:scale-105 active:scale-95
                         transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed
                         focus:outline-none focus:ring-4 focus:ring-white/50"
              aria-label="Foto aufnehmen"
            >
              {isProcessing ? (
                <div className="w-8 h-8 border-4 border-neutral-300 border-t-neutral-800 rounded-full animate-spin" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500" />
              )}
            </button>
          </div>
        </>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* PREVIEW PHASE                                                        */}
      {/* ------------------------------------------------------------------ */}
      {phase === 'preview' && previewUrl && (
        <>
          {/* Vorschau-Bild */}
          <div className="relative flex-1 flex items-center justify-center bg-black">
            <img
              src={previewUrl}
              alt="Aufgenommenes Bild"
              className="max-w-full max-h-full object-contain animate-scale-in"
            />

            {/* Bild-Label */}
            <div className="absolute top-4 left-0 right-0 flex justify-center pointer-events-none">
              <span className="px-3 py-1 rounded-full bg-black/60 backdrop-blur-sm text-white/80 text-xs">
                Vorverarbeitet · Kontrast +30% · Scharf
              </span>
            </div>
          </div>

          {/* Action-Leiste */}
          <div className="relative z-10 flex items-center justify-between gap-4 px-6 py-6 bg-black/80 backdrop-blur-sm">
            {/* Nochmal */}
            <button
              onClick={retake}
              className="flex-1 flex flex-col items-center gap-1 py-3 rounded-2xl
                         bg-white/10 hover:bg-white/20 active:bg-white/5
                         text-white transition-all duration-150 active:scale-95
                         focus:outline-none focus:ring-2 focus:ring-white/30"
              aria-label="Foto nochmal aufnehmen"
            >
              <RotateCcw className="w-6 h-6" />
              <span className="text-xs font-medium">Nochmal</span>
            </button>

            {/* Verwenden */}
            <button
              onClick={handleUse}
              className="flex-1 flex flex-col items-center gap-1 py-3 rounded-2xl
                         bg-gradient-to-r from-primary-500 to-secondary-500
                         hover:shadow-glow-primary hover:scale-[1.02] active:scale-95
                         text-white font-semibold transition-all duration-150
                         focus:outline-none focus:ring-2 focus:ring-primary-400/50"
              aria-label="Foto verwenden"
            >
              <Check className="w-6 h-6" />
              <span className="text-xs font-medium">Verwenden</span>
            </button>
          </div>

          {/* Schliessen-Button in der Preview-Phase */}
          <button
            onClick={handleClose}
            className="absolute top-4 left-5 z-20 w-11 h-11 rounded-full bg-black/60 backdrop-blur-sm
                       flex items-center justify-center text-white
                       hover:bg-black/80 active:scale-95 transition-all duration-150"
            aria-label="Scanner schliessen"
          >
            <X className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Inline-CSS fuer die animierte Scan-Linie (kein externes Package noetig) */}
      <style>{`
        @keyframes scanLine {
          0%   { top: 5%; opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { top: 95%; opacity: 0; }
        }
        .scan-line {
          animation: scanLine 2.4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

// ---- Scan-Overlay SVG -------------------------------------------------------

/**
 * Zeichnet ein dunkles Overlay mit einem transparenten Rechteck in der Mitte.
 * Die gestrichelten Ecken werden als separate SVG-Elemente ueberlagert.
 */
const ScanOverlay: React.FC = () => {
  // Prozentuale Masse des Scan-Bereichs
  const frameW = 72; // % der Viewport-Breite
  const frameH = 55; // % der Viewport-Hoehe

  const marginX = (100 - frameW) / 2;
  const marginY = (100 - frameH) / 2;

  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="w-full h-full"
      aria-hidden="true"
    >
      <defs>
        <mask id="scan-mask">
          {/* Gesamte Flaeche weiss (sichtbar) */}
          <rect x="0" y="0" width="100" height="100" fill="white" />
          {/* Scan-Bereich schwarz (ausgestanzt) */}
          <rect
            x={marginX}
            y={marginY}
            width={frameW}
            height={frameH}
            rx="1.5"
            fill="black"
          />
        </mask>
      </defs>

      {/* Dunkel-Overlay nur ausserhalb des Rahmens */}
      <rect
        x="0" y="0" width="100" height="100"
        fill="rgba(0,0,0,0.55)"
        mask="url(#scan-mask)"
      />

      {/* Gestrichelte Ecken als Rahmen-Guides */}
      <CornerMarkers mx={marginX} my={marginY} fw={frameW} fh={frameH} />
    </svg>
  );
};

interface CornerMarkersProps {
  mx: number; my: number; fw: number; fh: number;
}

const CornerMarkers: React.FC<CornerMarkersProps> = ({ mx, my, fw, fh }) => {
  const len   = 5;   // Laenge der Eckenlinien in SVG-Einheiten
  const thick = 0.6; // Strichstaerke
  const color = '#818CF8'; // primary-400

  const corners: Array<[number, number, 'tl' | 'tr' | 'bl' | 'br']> = [
    [mx,        my,        'tl'],
    [mx + fw,   my,        'tr'],
    [mx,        my + fh,   'bl'],
    [mx + fw,   my + fh,   'br'],
  ];

  return (
    <>
      {corners.map(([cx, cy, pos]) => {
        const dx = pos.includes('r') ? -len : len;
        const dy = pos.includes('b') ? -len : len;
        return (
          <g key={pos}>
            {/* Horizontale Linie */}
            <line
              x1={cx} y1={cy}
              x2={cx + dx} y2={cy}
              stroke={color}
              strokeWidth={thick}
              strokeLinecap="round"
            />
            {/* Vertikale Linie */}
            <line
              x1={cx} y1={cy}
              x2={cx} y2={cy + dy}
              stroke={color}
              strokeWidth={thick}
              strokeLinecap="round"
            />
          </g>
        );
      })}
    </>
  );
};
